import {ref} from 'vue'
import {defineStore} from 'pinia'
import {config} from "@/env-config";
import {decodeJwt} from "jose";
import {AuthSpaClient, type ITokenExchangeResult} from "@dbg-health/auth-sdk";
import {buildState, parseState, splitOnWhitespace} from "../utils";
import {VALUE_RESOURCE, scopesFor} from "@/stores/resources";

const CLIENT_ID = '76ce7d4f-ed55-47ca-9f84-9bbb65543a6b';
const REDIRECT_URI = `${window.location.origin}/auth/callback`; // Must match registered redirect URI at auth server

export interface ILoginProps {
    targetUrl?: string;
    prompt?: string;
}

// Module-scoped non-reactive global mutex: only one /token refresh in flight
// at any time across all resources, since each refresh rotates the refresh token.
let globalRefreshPromise: Promise<boolean> | null = null;

export const useAuthStore = defineStore('auth', () => {
    const isAuthLoading = ref(false)
    const isLoggedIn = ref(false)
    const accessTokens = ref<Record<string, string>>({})
    const idToken = ref<string | undefined>('')
    const authBase = ref<string>(config.authHost)

    const authError = ref<string | undefined>('');
    const authErrorDescription = ref<string | undefined>('');
    const authRoles = ref<string[]>([]);

    const refreshToken = ref<string | undefined>(undefined);
    const refreshTokenResource = ref<string | undefined>(undefined);

    const authSpaClient = new AuthSpaClient({
        clientId: CLIENT_ID,
        issuerUri: config.authHost,
    });

    function setTokens(resource: string, result: ITokenExchangeResult) {
        if (!result.access_token) {
            throw new Error('Access token is missing in the token exchange result');
        }
        if (!result.refresh_token) {
            throw new Error('Refresh token is missing in the token exchange result');
        }

        accessTokens.value = {...accessTokens.value, [resource]: result.access_token};
        refreshToken.value = result.refresh_token;
        refreshTokenResource.value = resource;
        isLoggedIn.value = true;

        // Only the value resource carries identity/roles for this app.
        if (resource === VALUE_RESOURCE) {
            if (!result.id_token) {
                throw new Error('ID token is missing in the token exchange result');
            }
            idToken.value = result.id_token;

            const decodedIdToken = decodeJwt(result.id_token);
            authRoles.value = splitOnWhitespace(decodedIdToken.roles as string);

            if ((window as any).clarity) {
                (window as any).clarity("identify", decodedIdToken.sub);
            }
        }
    }

    function clearTokens() {
        refreshToken.value = undefined;
        refreshTokenResource.value = undefined;
        accessTokens.value = {};
        idToken.value = '';
        authRoles.value = [];
        isLoggedIn.value = false;
    }

    function getAccessToken(resource: string): string | undefined {
        return accessTokens.value[resource];
    }

    async function doTokenRefresh(resource: string): Promise<boolean> {
        // If a refresh is already in flight, wait for it; if it gave us what we need, we're done.
        // Otherwise fall through and start our own.
        while (globalRefreshPromise) {
            await globalRefreshPromise;
            const token = accessTokens.value[resource];
            if (token && !authSpaClient.isTokenExpired(token, 20)) {
                return true;
            }
        }

        if (!refreshToken.value) {
            return false;
        }

        const accessTokenHint = refreshTokenResource.value
            ? accessTokens.value[refreshTokenResource.value]
            : undefined;

        globalRefreshPromise = (async () => {
            try {
                const result = await authSpaClient.refreshToken({
                    refreshToken: refreshToken.value,
                    resource,
                    scopes: scopesFor(resource),
                    accessTokenHint,
                });
                if (!result) {
                    return false;
                }
                setTokens(resource, result);
                return true;
            } catch (e) {
                console.error(`Token refresh for ${resource} failed:`, e);
                return false;
            }
        })();

        try {
            return await globalRefreshPromise;
        } finally {
            globalRefreshPromise = null;
        }
    }

    async function ensureFreshAccessToken(resource: string): Promise<boolean> {
        const token = accessTokens.value[resource];
        if (token && !authSpaClient.isTokenExpired(token, 20)) {
            return true;
        }
        return doTokenRefresh(resource);
    }

    async function login(props?: ILoginProps): Promise<void> {
        const state = buildState({
            targetUrl: props?.targetUrl || '/about'
        });
        try {
            isAuthLoading.value = true;
            // redirect to the auth server for login
            await authSpaClient.authorize({
                redirectUri: REDIRECT_URI,
                scopes: scopesFor(VALUE_RESOURCE),
                resource: VALUE_RESOURCE,
                state,
                prompt: props?.prompt
            }).then(_ => {});
        } catch (e: any) {
            console.error('Error during login:', e);
            authErrorDescription.value = e.message || 'An error occurred during login';
            isLoggedIn.value = false;
            clearTokens();
            throw e;
        } finally {
            isAuthLoading.value = false
        }
    }

    async function logout(): Promise<void> {
        isAuthLoading.value = true;

        try {
            isLoggedIn.value = false;
            const idTokenHint = idToken.value;
            clearTokens();

            // Calculate post-logout redirect URI
            const currentOrigin = window.location.origin;
            const postLogoutRedirectUri = `${currentOrigin}/welcome`;

            await authSpaClient.endSession({
                idTokenHint,
                postLogoutRedirectUri
            });
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            isAuthLoading.value = false;
        }
    }

    async function silentLogin(): Promise<boolean> {
        isAuthLoading.value = true;

        try {
            isLoggedIn.value = false;

            const result = await authSpaClient.authorizeSilently({
                resource: VALUE_RESOURCE,
                scopes: scopesFor(VALUE_RESOURCE),
            });
            if (result) {
                setTokens(VALUE_RESOURCE, result);
                return true;
            } else {
                clearTokens();
                return false;
            }
        } catch (e: any) {
            console.error('Error during silent login:', e);
            authErrorDescription.value = e.message || 'An error occurred during silent login';
            clearTokens();
            return false;
        } finally {
            isAuthLoading.value = false
        }
    }

    async function handleCallback(code: string, rawState: string): Promise<string> {
        const result = await authSpaClient.handleCallback({
            code,
            redirectUri: REDIRECT_URI,
            resource: VALUE_RESOURCE,
        });

        if (result) {
            setTokens(VALUE_RESOURCE, result);
            const parsedState = parseState(rawState);
            return parsedState.targetUrl || '/about';
        } else {
            clearTokens();
            throw new Error('Failed to handle OAuth callback');
        }
    }

    function userInfo() {
        if (!isLoggedIn.value) {
            return null;
        }

        const decodedIdToken = decodeJwt(idToken.value!);

        return {
            username: decodedIdToken.sub,
            email: decodedIdToken.email,
            firstName: decodedIdToken.given_name,
            lastName: decodedIdToken.family_name
        };
    }

    function hasRole(role: string): boolean {
        if (!role) {
            return false;
        }
        return authRoles.value.includes(role);
    }

    async function ensureValueAccessOrLogin(): Promise<void> {
        if (await ensureFreshAccessToken(VALUE_RESOURCE)) {
            return;
        }
        // No valid value token and refresh failed → start login flow.
        // After login, try to land back on the page we're on.
        return login({targetUrl: window.location.pathname.replace("/ui/", "/")});
    }

    return {
        isAuthLoading,
        isLoggedIn,
        accessTokens,
        idToken,
        authBase,
        authError,
        authErrorDescription,
        authRoles,

        getAccessToken,
        ensureFreshAccessToken,
        ensureValueAccessOrLogin,
        silentLogin,
        login,
        logout,
        handleCallback,
        userInfo,
        hasRole,
    }
})
